import type { WithingsMeasurement } from "../../types/database";

import React from "react";

import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";

type DateRange = "1y" | "7d" | "30d" | "90d";

type TrendDirection = "negative" | "neutral" | "positive";

interface TrendData {
  change: number;
  firstDate: string;
  lastDate: string;
  percentage: number;
  trend: TrendDirection;
}

interface MetricCardProperties {
  dateRange: DateRange;
  isGoodPositive?: boolean;
  latest: undefined | WithingsMeasurement;
  title: string;
  trend: TrendData;
  unit: string;
  valueFormatter?: (value: number) => string;
  valueKey: keyof WithingsMeasurement;
}

/**
 * Helper function to get trend badge
 *
 * @param trend
 *
 * @param isGoodPositive
 *
 * @returns {JSX.Element} Badge component with trend icon and variant
 *
 */
const getTrendBadge = (trend: TrendDirection, isGoodPositive = false): React.ReactElement => {
  const isGood = isGoodPositive ? trend === "positive" : trend === "negative";
  const variant = (() => {
    if (isGood) {
      return "success";
    }
    if (trend === "positive") {
      return "destructive";
    }
    return "default";
  })();
  const icon = (() => {
    if (trend === "positive") {
      return "↗️";
    }
    if (trend === "negative") {
      return "↘️";
    }
    return "→";
  })();

  return (
    <Badge size="sm" style="subtle" variant={variant}>
      {icon}
    </Badge>
  );
};

/**
 * Format date range text
 *
 * @param range
 *
 * @returns {string} Formatted date range text
 *
 */
const formatDateRangeText = (range: DateRange): string => {
  if (range === "7d") {
    return "7 days";
  }
  if (range === "30d") {
    return "30 days";
  }
  if (range === "90d") {
    return "90 days";
  }
  return "1 year";
};

/**
 * Metric Card Component
 * Displays a single health metric with trend information
 *
 * @param root0
 *
 * @param root0.dateRange
 *
 * @param root0.latest
 *
 * @param root0.title
 *
 * @param root0.trend
 *
 * @param root0.unit
 *
 * @param root0.valueKey
 *
 * @param root0.valueFormatter
 *
 * @param root0.isGoodPositive
 *
 * @returns {JSX.Element} Metric card component
 *
 */
export const MetricCard: React.FC<MetricCardProperties> = ({
  dateRange,
  isGoodPositive = false,
  latest,
  title,
  trend,
  unit,
  valueFormatter,
  valueKey,
}) => {
  const getValue = (): string => {
    if (!latest) {
      return "N/A";
    }

    const value = latest[valueKey] as null | number | undefined;

    if (value === null || value === undefined || typeof value !== "number") {
      return "N/A";
    }

    if (valueFormatter) {
      return `${valueFormatter(value)} ${unit}`;
    }

    return `${value.toFixed(1)} ${unit}`;
  };

  const getTrendColor = (): string => {
    if (isGoodPositive) {
      return trend.trend === "positive" ? "text-green-400" : "text-red-400";
    }
    return trend.trend === "positive" ? "text-red-400" : "text-green-400";
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700 text-white hover:bg-slate-800/70 transition-all duration-200">
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <img
              alt="Withings"
              className="h-5 w-5 object-contain opacity-80"
              src="/src/assets/withings-logo.png"
            />
            <span className="text-slate-400 text-sm font-medium">{title}</span>
          </div>
          {getTrendBadge(trend.trend, isGoodPositive)}
        </div>
        <p className="text-3xl font-bold text-white mb-2">{getValue()}</p>
        {trend.change !== 0 && (
          <div className="flex items-center space-x-2">
            <span className={`text-sm font-medium ${getTrendColor()}`}>
              {trend.trend === "positive" ? "+" : ""}
              {trend.change.toFixed(1)} {unit}
            </span>
            <span className="text-xs text-slate-500">in {formatDateRangeText(dateRange)}</span>
          </div>
        )}
        {trend.change === 0 && <span className="text-xs text-slate-500">No change</span>}
        <div className="text-xs text-slate-500 mt-1">
          {trend.percentage !== 0 && (
            <span className={getTrendColor()}>
              {trend.trend === "positive" ? "+" : ""}
              {trend.percentage.toFixed(1)}%
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};
